import type { ReactElement } from 'react';
import { Link } from 'react-router';
import { Button, EmptyState, Skeleton, cn } from '@xdocs/ui';
import { ReferralPanel } from '../../shares/ReferralPanel.js';
import { useReferralStats } from './useReferralStats.js';
import { useSettingsReferralsT } from './useSettingsReferralsT.js';
import styles from './ReferralsSettingsPage.module.css';

/** Literal reward count for this page's copy — kept here (not imported)
 * because `apps/web` has no dependency on `@xdocs/core`, where the real
 * `REFERRAL_REWARD_CREDITS` constant lives. Keep in sync if that ever changes. */
const REFERRAL_REWARD_CREDITS_COPY = 10;

/**
 * `/settings/referrals` (F72). ARCHITECTURE NOTE — deliberately deviates
 * from the `design/app/27-settings.html` mockup: the mockup shows a "REFER A
 * FRIEND" widget with a personal `xdocs.app/r/ada-9f3a`-style minted code and
 * a fictional 20/20/+50 reward schedule. The REAL, already-built mechanism
 * (see the top doc comments of `apps/web/src/features/sharing/referralUrl.ts`
 * and `apps/server/src/shares/referral.ts`) carries referral attribution on
 * a SHARE's own public URL (`ref={shares.id}`) — there is no separately
 * minted personal code — and the real reward is
 * `REFERRAL_REWARD_CREDITS = 10` (`packages/core/src/referral-rewards.ts`),
 * not the mockup's 20/20/50. So this page is a STATUS VIEW, per FEATURES.md
 * F72's own prose ("a referral status view — e.g. in `/settings/billing`
 * credits section or `/activity`") — not a code generator: an explanation of
 * the real flow + a CTA into the document library (where a share is
 * actually created; `apps/web/src/routes/shares/SharesPage.tsx`'s empty
 * state already says "Share a document from its page to see it here") + the
 * REUSED `<ReferralPanel>` (`apps/web/src/routes/shares/ReferralPanel.tsx`)
 * for the detailed per-referral status list.
 */
export function ReferralsSettingsPage(): ReactElement {
  const { t } = useSettingsReferralsT();
  const { stats, isLoading: statsLoading, isError: statsError, onRetry: retryStats } = useReferralStats();

  return (
    <div className={cn(styles['page'])}>
      <div className={cn(styles['header'])}>
        <h1 className={cn(styles['title'])}>{t('settingsReferrals:title')}</h1>
        <p className={cn(styles['description'])}>{t('settingsReferrals:description', { count: REFERRAL_REWARD_CREDITS_COPY })}</p>
        <Link to="/documents">
          <Button variant="primary">{t('settingsReferrals:cta.label')}</Button>
        </Link>
      </div>

      {/* Stat tiles degrade independently of the panel below — a stats-fetch
          error never blocks `<ReferralPanel>` from rendering its own list. */}
      <div className={cn(styles['statsRow'])} aria-label={t('settingsReferrals:panel.title')}>
        {statsLoading && (
          <>
            <div aria-busy="true" className={cn(styles['statTile'])}>
              <Skeleton shape="text-line" width="50%" />
            </div>
            <div aria-busy="true" className={cn(styles['statTile'])}>
              <Skeleton shape="text-line" width="50%" />
            </div>
            <div aria-busy="true" className={cn(styles['statTile'])}>
              <Skeleton shape="text-line" width="50%" />
            </div>
          </>
        )}

        {!statsLoading && statsError && (
          <div className={cn(styles['statsError'])}>
            <EmptyState
              kind="error"
              title={t('settingsReferrals:stats.error.title')}
              action={{ label: t('settingsReferrals:stats.action.retry'), onClick: retryStats }}
            />
          </div>
        )}

        {!statsLoading && !statsError && stats && (
          <>
            <div className={cn(styles['statTile'])}>
              <span className={cn(styles['statValue'])}>{stats.invited}</span>
              <span className={cn(styles['statLabel'])}>{t('settingsReferrals:stats.invited.label')}</span>
            </div>
            <div className={cn(styles['statTile'])}>
              <span className={cn(styles['statValue'])}>{stats.joined}</span>
              <span className={cn(styles['statLabel'])}>{t('settingsReferrals:stats.joined.label')}</span>
            </div>
            <div className={cn(styles['statTile'])}>
              <span className={cn(styles['statValue'])}>{stats.creditsEarnedTotal}</span>
              <span className={cn(styles['statLabel'])}>{t('settingsReferrals:stats.creditsEarned.label')}</span>
            </div>
          </>
        )}
      </div>

      <ReferralPanel />
    </div>
  );
}
